import type React from "react";
import { X } from "lucide-react";
import { useState, useEffect } from "react";
import useApiHook from "../../../../utils/useApi";
import {
  ApiLogResponse,
  TransactionLog,
  TransactionLogsModalProps,
} from "../../../../interface/interfaces";
import { formatDateTime } from "../../../../utils/utils";

const TransactionLogsModal: React.FC<TransactionLogsModalProps> = ({
  reference,
  isOpen,
  onClose,
}) => {
  const [logs, setLogs] = useState<TransactionLog[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { isLoading, makeRequest } = useApiHook<ApiLogResponse>(
    `/dashboard/payment/log/${reference}`,
    {
      method: "get",
    }
  );

  useEffect(() => {
    if (isOpen && reference) {
      fetchLogs();
    }
  }, [isOpen, reference]);

  const fetchLogs = async () => {
    setError(null);

    try {
      const response = await makeRequest();

      if (response?.data?.statusCode === "00") {
        setLogs(response.data.data);
      } else if (response?.data?.statusCode === "08") {
        setError("no logs available for this transaction");
      } else {
        setError(response?.data?.description || "Failed to fetch logs");
      }
    } catch (err) {
      setError("An error occurred while fetching logs");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-auto bg-white rounded-lg shadow-xl">
        <div className="sticky top-0 flex items-center justify-between p-4 border-b bg-white z-50">
          <h2 className="text-xl font-bold text-gray-900">Transaction Logs</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="mb-4 p-4 bg-gray-50 border border-gray-100 rounded-lg">
                <h3 className="font-medium text-gray-800">
                  Transaction Reference: {reference}
                </h3>
              </div>

              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                <ul className="space-y-4">
                  {logs.map((log, index) => (
                    <li key={index} className="relative pl-8 z-[10]">
                      <div
                        className={`absolute left-0 top-2 w-6 h-6 border-2 rounded-full -z-10 ${
                          log.log === "Completed payment process"
                            ? "bg-green-100 border-green-400"
                            : log.log.includes("Failed")
                            ? "bg-red-100 border-red-400"
                            : "bg-white border-gray-300"
                        }`}
                      ></div>
                      <div className="p-3 bg-white border rounded-lg shadow-sm">
                        <p className="font-medium text-gray-900">{log.log}</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {formatDateTime(log.dateCreated)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionLogsModal;
